import PropTypes from 'prop-types';
import { useEffect, useState, useCallback } from 'react';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { Button, Grid, Typography, CircularProgress } from '@mui/material';

// third-party
import { useDropzone } from 'react-dropzone';
import { FormattedMessage } from 'react-intl';

// project hooks
import useAuth from 'hooks/useAuth';

// assets
import FileUploadIcon from '@mui/icons-material/FileUpload';

const DropZoneStyle = styled('div')(({ theme }) => ({
    borderStyle: 'dashed',
    borderWidth: 2,
    borderColor: theme.palette.grey[500],
    fontSize: 24,
    cursor: 'pointer',
    margin: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    '&:hover': { opacity: 0.72 }
}));

// ==============================|| FILE UPLOADER ||============================== //

const FileUploader = ({
    handleAddFiles = () => {},
    formIsSubmitting = false,
    text,
    size,
    direction,
    clearAfterAdd,
    accept,
    multiple = true,
    handleRejectedFiles = () => {},
    ...others
}) => {
    const theme = useTheme();
    const { user } = useAuth();

    const [files, setFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    const handleDropFiles = useCallback(
        (droppedFiles) =>
            droppedFiles.forEach((file) => {
                const reader = new FileReader();
                const newFile = {
                    fileMimeType: file?.type || '',
                    fileOriginalName: file?.name || '',
                    fileSize: file?.size || '',
                    createdAt: new Date(),
                    createdBy: `${user?.name || ''} ${user?.lastName || ''}`,
                    file,
                    isBeingAdded: true
                };

                reader.onloadstart = () => setIsUploading(true);
                reader.onloadend = () => setIsUploading(false);
                reader.onload = (e) => {
                    newFile.preview = e.target.result || '';
                    setFiles((prevFiles) => [...prevFiles, newFile]);
                };
                reader.readAsDataURL(file);
            }),
        [user]
    );

    useEffect(() => {
        if (files?.length > 0) {
            handleAddFiles(files);

            if (clearAfterAdd) setFiles([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleDropFiles,
        onDropRejected: handleRejectedFiles,
        accept: accept || undefined,
        multiple
    });

    return (
        <DropZoneStyle {...getRootProps()} sx={{ ...(isDragActive && { opacity: 0.72 }) }}>
            <input {...getInputProps()} disabled={formIsSubmitting} multiple={multiple} {...others} />
            <Button size={size || 'large'} fullWidth sx={{ p: 1.5, color: theme.palette.grey[500] }} disabled={formIsSubmitting}>
                <Grid
                    container
                    direction={direction || 'column'}
                    justifyContent={direction === 'row' ? 'space-between' : 'center'}
                    alignItems="center"
                >
                    <Grid item>
                        {isUploading || formIsSubmitting ? (
                            <CircularProgress color="primary" size={26} />
                        ) : (
                            <FileUploadIcon fontSize={size || 'medium'} />
                        )}
                    </Grid>
                    <Grid item>
                        {text || (
                            <Typography variant="caption" sx={{ textTransform: 'none' }}>
                                <FormattedMessage id="ui-comp-formvalidation-file-maxupload" />
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            </Button>
        </DropZoneStyle>
    );
};

FileUploader.propTypes = {
    handleAddFiles: PropTypes.func,
    formIsSubmitting: PropTypes.bool,
    clearAfterAdd: PropTypes.bool,
    accept: PropTypes.string,
    multiple: PropTypes.bool,
    handleRejectedFiles: PropTypes.func,
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    size: PropTypes.string,
    direction: PropTypes.string
};

export default FileUploader;
